import React, { useContext, useRef, useState } from "react";
import ReusableDialog from "../../Dialog";
import {
  Box,
  DialogContent,
  Button,
  Stack,
  Typography,
  TextField,
  DialogActions,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { submitContactInquiryApi } from "../../../utils/apicalls";
import { trackEvent } from "../../../utils/analytics";
import { UIContext, UIState } from "../../../providers/UIProvider";
import { getApiErrorMessage } from "../../../utils/apiError";

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

export type ContactAnalyticsContext = {
  source: string;
  audience?: string;
  community_name?: string;
  has_quote?: boolean;
};

type ContactUsProps = {
  buttonLabel?: string;
  buttonLabelMobile?: string;
  defaultMessage?: string;
  dialogTitle?: string;
  dialogSubtitle?: string;
  analyticsContext?: ContactAnalyticsContext;
  onDialogOpen?: () => void;
};

const ContactUs: React.FC<ContactUsProps> = ({
  buttonLabel: buttonLabelProp,
  buttonLabelMobile: buttonLabelMobileProp,
  defaultMessage = "",
  dialogTitle: dialogTitleProp,
  dialogSubtitle: dialogSubtitleProp,
  analyticsContext,
  onDialogOpen,
}) => {
  const { t } = useTranslation();
  const buttonLabel = buttonLabelProp ?? t("contact.buttonLabel");
  const buttonLabelMobile = buttonLabelMobileProp ?? t("contact.buttonLabelMobile");
  const dialogTitle = dialogTitleProp ?? t("contact.dialogTitle");
  const dialogSubtitle = dialogSubtitleProp ?? t("contact.dialogSubtitle");
  const [open, setOpen] = useState(false);
  const [, dispatch] = useContext<UIState | any>(UIContext);
  const submittedSuccessfullyRef = useRef(false);
  const formStartedRef = useRef(false);
  const source = analyticsContext?.source ?? "unknown";

  const validationSchema = Yup.object({
    name: Yup.string().trim().required(t("contact.validation.nameRequired")),
    email: Yup.string()
      .email(t("contact.validation.emailInvalid"))
      .required(t("contact.validation.emailRequired")),
    phone: Yup.string().trim(),
    message: Yup.string()
      .trim()
      .min(10, t("contact.validation.messageMin"))
      .required(t("contact.validation.messageRequired")),
  });

  const sharedParams = () => ({
    source,
    cta_label: buttonLabel,
    ...(analyticsContext?.audience
      ? { audience: analyticsContext.audience }
      : {}),
    ...(analyticsContext?.community_name
      ? { community_name: analyticsContext.community_name }
      : {}),
    ...(analyticsContext?.has_quote != null
      ? { has_quote: analyticsContext.has_quote }
      : {}),
  });

  const handleSetOpen = (nextOpen: boolean) => {
    if (nextOpen && !open) {
      submittedSuccessfullyRef.current = false;
      formStartedRef.current = false;
      trackEvent("contact_form_open", sharedParams());
      onDialogOpen?.();
    } else if (!nextOpen && open) {
      if (!submittedSuccessfullyRef.current) {
        trackEvent("contact_form_abandon", {
          ...sharedParams(),
          had_interaction: formStartedRef.current,
        });
      }
    }
    setOpen(nextOpen);
  };

  const handleFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent("contact_form_start", sharedParams());
  };

  const handleSubmit = (
    values: ContactFormValues,
    { setSubmitting, resetForm }: any
  ) => {
    submitContactInquiryApi(
      values,
      () => {
        submittedSuccessfullyRef.current = true;
        trackEvent("contact_form_submit", sharedParams());
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: t("contact.success"),
            severity: "success",
          },
        });
        setSubmitting(false);
        resetForm();
        handleSetOpen(false);
      },
      (error) => {
        console.error("Error submitting contact form:", error);
        trackEvent("contact_form_submit_error", sharedParams());
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message:
              typeof error === "string"
                ? error
                : getApiErrorMessage({ response: { data: error } }, t, "contact.error"),
            severity: "error",
          },
        });
        setSubmitting(false);
      }
    );
  };

  return (
    <ReusableDialog
      setIsOpen={handleSetOpen}
      isOpen={open}
      maxWidth="sm"
      content={
        <Formik
          initialValues={{
            name: "",
            email: "",
            phone: "",
            message: defaultMessage,
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    width: "100%",
                    backgroundColor: "transparent",
                    p: { xs: 0, sm: 1 },
                  }}
                >
                  <Typography
                    variant="h4"
                    textAlign="center"
                    gutterBottom
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      display: { xs: "none", sm: "block" },
                      fontSize: { sm: "1.75rem", md: "2rem" },
                    }}
                  >
                    {dialogTitle}
                  </Typography>
                  <Typography
                    variant="body1"
                    textAlign="center"
                    gutterBottom
                    sx={{
                      color: "#94a3b8",
                      mb: 3,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    {dialogSubtitle}
                  </Typography>
                  <Stack spacing={2}>
                    <Field
                      as={TextField}
                      name="name"
                      label={t("contact.fullName")}
                      variant="outlined"
                      fullWidth
                      onFocus={handleFormStart}
                      error={Boolean(touched.name && errors.name)}
                      helperText={<ErrorMessage name="name" />}
                    />
                    <Field
                      as={TextField}
                      name="email"
                      label={t("contact.email")}
                      variant="outlined"
                      type="email"
                      fullWidth
                      onFocus={handleFormStart}
                      error={Boolean(touched.email && errors.email)}
                      helperText={<ErrorMessage name="email" />}
                    />
                    <Field
                      as={TextField}
                      name="phone"
                      label={t("contact.phone")}
                      variant="outlined"
                      type="tel"
                      fullWidth
                      onFocus={handleFormStart}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={<ErrorMessage name="phone" />}
                    />
                    <Field
                      as={TextField}
                      name="message"
                      label={t("contact.message")}
                      variant="outlined"
                      multiline
                      rows={4}
                      fullWidth
                      onFocus={handleFormStart}
                      error={Boolean(touched.message && errors.message)}
                      helperText={<ErrorMessage name="message" />}
                    />
                  </Stack>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack spacing={2} sx={{ width: "100%" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("contact.sending") : t("contact.send")}
                  </Button>
                </Stack>
              </DialogActions>
            </Form>
          )}
        </Formik>
      }
      buttonVariant="contained"
      buttonText={buttonLabel}
      buttonTextMobile={buttonLabelMobile}
      title={dialogTitle}
      buttonColor="primary"
    />
  );
};

export default ContactUs;
